/**
 * Copyright (c) 2018-present clchart Contributors.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

// 以下是ClData的实体定义
// 一般只用操作这个类就可以获取单个股票的所有数据
// 不支持多个股票的数据，对于列表来说，这里只保存ID列表，其他的数据由实际画图自行获取，

import {
    FIELD_ILINE,
} from '../cl.chart.res'
import {
    getDate,
    isEmptyArray,
    copyArrayOfDeep,
    copyJsonOfDeep,
} from '../cl.utils'
import {
    ClFormula
} from './cl.formula'
// 只保存一只股票的信息，当前日期，开收市时间

export const STOCK_TYPE_IDX = 0
export const STOCK_TYPE_STK = 1

export const FIELD_TRADETIME = {
    open: 0,
    stop: 1
}
export const STOCK_TRADETIME = [
    [930, 1130],
    [1300, 1500]
]

const DEFAULT_LINKINFO = {
    // 公共参数
    curKey : 'SH600600',    // 当前股票
    static : {
        name    : '青岛啤酒',
        coindot : 2,
        style   : STOCK_TYPE_STK,
        agoprc  : 10.0,
    },

    showCursorLine: false,  // 是否显示光标线信息
    showCursorInfo: true,   // 是否显示光标的移动信息 滑动框

    // 表格需要临时保存的信息
    sortFieldIdx : -1,      
    sortFieldMode : 'preface',  
    selectIndex   : -1,

    // 可以自由伸缩的线图参数
    zoomIndex : -1,     // 默认从 ZOOM_INFO_DEF 的index获取
    maxCount:  0,       // 当前画面总共的可见记录数
    minIndex: -1,       // 当前画面的起始记录号 -1 表示第一次
    maxIndex: -1,       // 当前画面的最大记录号 -1 表示第一次
    spaceX: 2,          // 每个数据的间隔像素，可以根据实际情况变化，但不能比系统参数里设定的spaceX小
    unitX: 5,           // 每天数据的宽度 默认为5， 可以在1..50之间切换
    
    // moveIndex: -1,       // 当前鼠标所在位置的记录号 -1 表示没有鼠标事件或第一次
    
    cursor : {    // 鼠标的移动光标信息
        moveIndex : -1,
        mousePos : {x : 0, y: 0},
        // axisXvar : '', 
    },

    showMode: 'init', // 显示模式
    // 'init'   默认第一次进入程序或者切换数据源时 显示最新的记录 用于 kbar等可缩放的图
    // 'move'   滑块移动时的显示方式 用于 kbar等可缩放的图
    // 'locked' 据有锁图功能的显示方式 比如需要分析历史某段时间的数据 默认锁记录位于中间 用于 kbar等可缩放的图
    // 'fixed'  固定坐标区域大小的显示方式 主要用于 minute 和 多日走势等固定大小的图
    // 'window' 固定窗口的现实方式 用于调仓分析

    fixed: { // 对应fixed模式
        min: -1, // 最小记录, 为-1表示最小记录加上left
        max: -1, // 最大记录, 为-1表式最大记录减去right
        left: 20,
        right: 20
    },
    scroll: { // 只有当showMode=='scroll' 模式才有作用
        index: -1, // 当前滑块的记录号
        set: 0.5   // 表示滑块在中间，1 表示滑块在最后一条记录，当前记录的百分比
    },
    locked: { // 只有当showMode=='lock'模式才有作用
        minXinfo: 0,   // 当前锁定的开始日期
        maxXinfo: 0   // 当前锁定的结束日期
    },
    // 固定坐标区域大小的显示方式 主要用于 minute 和 多日走势等固定大小的图
    // maxCount:  0,       // 当前画面总共的可见记录数
    // minIndex: -1,       // 当前画面的起始记录号 -1 表示第一次
    // maxIndex: -1,       // 当前画面的最大记录号 -1 表示第一次
    // moveIndex: -1,       // 当前鼠标所在位置的记录号 -1 表示没有鼠标事件或第一次
    // spaceX: 2,          // 每个数据的间隔像素，可以根据实际情况变化，但不能比系统参数里设定的spaceX小
    // unitX: 5,           // 每天数据的宽度 默认为5， 可以在1..50之间切换

}

/**
 * Class representing ClData
 * data layer
 * @export
 * @class ClData
 */
export default class ClDatas {
    /**
     * Creates an instance of ClData.
     * 这里保存所有数据 
     */
    constructor(codeInfo) {
        this.formula = new ClFormula();

        // 需要把所有的股票代码 按 stkInfo['SH600600'] = {} 这种方式生成一个 stkInfo 字典传入 chart 中
        // 例子 SH600600 = { static : [[]], frights : [[]] }
        // 按股票名称写入 这个需要统一起来 不再支持 setBaseInfo setFrightInfo 
        // this.linkInfo = {}

        this.keyInfo = {}
        if (codeInfo !== undefined) {
            this.keyInfo = codeInfo
        }
        // keyInfo 是所有股票的信息
        // static 是当前股票的基本信息
        
        // linkInfo 是所有子chart公用的参数集合 需要保存并传递
        this.linkInfo = copyJsonOfDeep(DEFAULT_LINKINFO)
        // 设置交易时间和日期
        this.tradeTime = STOCK_TRADETIME
        this.tradeDate = getDate()
        this.axisXinfo = {}

        this.clearData()
    }

    // 包含一个股票所有的数据,以便于以后做公式系统也使用这个数据定义
    /**
     * clear data
     * @memberof ClData
     */
    clearData() {
        this.InData = [] // 数据 json格式数据 {key:..,fields:.., value:[[],[]...]}
        this.OutData = [] // 专门用于获取数据时临时产生的数据
        this.axisXinfo = {}
        // this.axisYdata = { min: 0, max: 1, unitY : 0.001 }  // 根据数据和窗口不同随时变化 不放数据层 由各个chart自行管理
    }
    // 下面是设置数据的方法
    /**
     * set data
     * @param {String} key
     * @param {Object} fields
     * @param {Array} value
     * @memberof ClData
     */
    setData(key, fields, value) {
        if (value === undefined) value = []
        this.InData[key] = {
            key,
            fields
        }
        // console.log(key);
        
        this.InData[key].value = copyArrayOfDeep(value)
    }
    // 下面是获取数据的方法,先从OutData获取，没有数据就从InData数据中获取
    /**
     * get data
     * @param {String} key
     * @return {Array}
     * @memberof ClData
     */
    getData(key) {
        return this.OutData[key] ? this.OutData[key] : this.InData[key]
    }
    //  以下为公式系统，自由计算的定义
    /**
     * make lien data
     * @param {Object} source
     * @param {String} outkey
     * @param {String} formula
     * @return {Array}
     * @memberof ClData
     */
    makeLineData(source, outkey, formula) {
        const value = this.formula.runSingleStock(source, formula)
        // console.log(outkey);
        
        if (this.OutData[outkey] === undefined) {
            this.OutData[outkey] = {
                outkey,
                fields: FIELD_ILINE,
                value
            }
        } else {
            this.OutData[outkey].value = value
            // 返回的值都是真实的值，不用再除单位了，具体显示几个小数点，由坐标的类别来定，
        }
        // console.log(outkey, this.OutData[outkey]);
        return this.OutData[outkey]
    }
}

// 按fields定义从数组value中获取，第index条标记为label的数据
/**
 * get value
 * @export
 * @param {Object} {
 *   fields,
 *   value,
 * }
 * @param {String} label
 * @param {Number} [index=0]
 * @return {Number}
 */
export function getValue({
    fields,
    value
}, label, index = 0) {
    let val = 0
    try {
        // 支持二维数组和一维数组，判断如果是二维数组就取对应的值
        let source = value
        if (Array.isArray(value[0])) {
            if (index === -1 || index > value.length - 1) {
                source = value[value.length - 1]
            }
            else {
                source = value[index]
            }
        }
        switch (label) {
            case 'index':
                // 序列，now 0-239，day5 0-5*239
                // 非常重要，利用这里可以统一MIN和D5的画图
                if (fields.index === undefined) val = index
                else val = source[fields.index]
                break
            default:
                if (source[fields[label]]) {
                    val = source[fields[label]]
                }
        }
    } catch (error) {
        val = 0
    }
    return val
}
export function getValueFidx({
    fields,
    value
}, fidx, ridx = 0) {
    let val = 0
    try {
        // 支持二维数组和一维数组，判断如果是二维数组就取对应的值
        let source = value
        if (Array.isArray(value[0])) {
            if (ridx === -1) {
                source = value[value.length - 1]
            }
            else {
                source = value[ridx]
            }
        }
        if (source[fidx]) {
            val = source[fidx]
        }
    } catch (error) {
        val = 0
    }
    return val
}

